/* @noflow FIXME */
import * as React from 'react';
import cx from 'classnames';
import { connect } from 'react-redux';
import gql from 'graphql-tag';
import { graphql, compose } from 'react-apollo';

import Glyph from 'components/Glyph';
import IndividualChatStarter from 'components/IndividualChatStarter';
import Link from 'components/Link';
import List from 'components/List';
import ListItem from 'components/ListItem';
import MediaObject from 'components/MediaObject';
import ModalTrigger from 'components/ModalTrigger';
import PhoneCallNoteModal from 'phoneCalls/components/PhoneCallNoteModal';
import Text from 'components/Text';
import TextWithGlyph from 'components/TextWithGlyph';
import Timer from 'components/Timer';
import User from 'entities/User';
import UserAvatar from 'users/components/UserAvatar';

import './styles/PhoneCallFloatingBox.scss';

const STATUS_TO_HUMAN_STATUS = {
  in_progress: 'In progress...',
  outgoing: 'Call ended',
  incoming: 'Call ended',
  missed: 'Call ended',
  failed: 'Call ended',
  canceled: 'Call ended',
  unknown: 'Call ended',
};

type Theme = 'in_progress' | 'terminated';

type Props = PhoneCallFloatingBoxQuery & {
  // the phone call uuid.
  uuid: string,
  // a callback to close the box.
  remove: Function,
  // the last time we received a push event for the phone call
  lastPhoneCallPushEvent?: ?Date,
  // apollo - refetch the phone call
  refetchPhoneCall: Function,
};

class PhoneCallFloatingBox extends React.Component<Props> {
  static WIDTH = 400;
  static HEIGHT = 123;
  static POSITION = 'top-right';
  static SUPPRESS_TEXT_SELECTION = true;

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.lastPhoneCallPushEvent !== this.props.lastPhoneCallPushEvent) {
      this.props.refetchPhoneCall();
    }
  }

  render() {
    if (!this.props.phoneCall) {
      return null;
    }

    const { phoneCall } = this.props;
    const theme: Theme =
      phoneCall.status_localization_key === 'in_progress' ? 'in_progress' : 'terminated';

    const classnames = cx('PhoneCallFloatingBox-call', {
      'PhoneCallFloatingBox-call--brand': theme === 'in_progress',
      'PhoneCallFloatingBox-call--light': theme === 'terminated',
    });

    const canChat = phoneCall.otherUser.chat_eligibility_state === 'eligible';

    return (
      <div className="PhoneCallFloatingBox">
        <div className={classnames}>
          <div className="PhoneCallFloatingBox-call-info">
            <div>
              <MediaObject center gutter="small">
                <UserAvatar user={phoneCall.otherUser} size="medium" className="Avatar--grey" />
                <div>
                  <Text size="medium" element="div">
                    {phoneCall.otherUser.name}
                  </Text>
                  <Text weight="light" element="div">
                    {STATUS_TO_HUMAN_STATUS[phoneCall.status_localization_key]}
                  </Text>
                </div>
              </MediaObject>
            </div>
          </div>
          <div className="PhoneCallFloatingBox-call-duration">
            {theme === 'in_progress' ? (
              <Text size="xlarge">
                <Timer start={new Date(phoneCall.started_at)} />
              </Text>
            ) : (
              <Glyph name="close" size="medium" onClick={this.props.remove} />
            )}
          </div>
        </div>
        <div className="PhoneCallFloatingBox-actions">
          <List size="medium">
            <ListItem>
              <ModalTrigger modal={<PhoneCallNoteModal uuid={this.props.uuid} />}>
                <Link>
                  <TextWithGlyph glyph="note" glyphSize="large">
                    {String(phoneCall.note).length === 0 && phoneCall.reasons.length === 0
                      ? 'Add call notes'
                      : 'Edit call notes'}
                  </TextWithGlyph>
                </Link>
              </ModalTrigger>
            </ListItem>
            {canChat &&
              theme === 'terminated' && (
                <ListItem>
                  <IndividualChatStarter
                    user={User.fromJSON(phoneCall.otherUser)}
                    onTransitionStart={this.props.remove}>
                    <TextWithGlyph glyph="chat-add-bubble" glyphSize="large">
                      Send Message
                    </TextWithGlyph>
                  </IndividualChatStarter>
                </ListItem>
              )}
          </List>
        </div>
      </div>
    );
  }
}

const PHONE_CALL_QUERY = gql`
  query PhoneCallFloatingBox($uuid: String!) {
    phoneCall(uuid: $uuid) {
      uuid
      reasons
      duration
      otherUser {
        id
        uuid
        name
        chat_eligibility_state
        initials
        profilePictureUrl
      }
      status_localization_key
      started_at
      status
      note
    }
  }
`;

const withPhoneCall = graphql(PHONE_CALL_QUERY, {
  options: ({ uuid }) => ({ variables: { uuid } }),
  props: ({ data }) => ({
    ...data,
    refetchPhoneCall: data.refetch,
  }),
});

export { PhoneCallFloatingBox };

function mapStateToProps(state: *, props: Props) {
  const phoneCall = state.phoneCalls.get(props.uuid);

  return {
    lastPhoneCallPushEvent: phoneCall ? phoneCall.received_at : undefined,
  };
}

export default compose(
  connect(mapStateToProps),
  withPhoneCall
)(PhoneCallFloatingBox);
